import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { fetchWebsites } from '../../store/slices/websiteSlice';
import TopNavigation from './TopNavigation';
import { WelcomeBackModal } from '../notifications/WelcomeBackModal';
import { notificationService } from '../../services/notificationService';

const MainLayout: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const { websites } = useAppSelector((state) => state.website);
  
  // Get current section from path
  const currentPath = location.pathname.slice(1) || 'dashboard';
  const [activeSection, setActiveSection] = useState(currentPath);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  
  useEffect(() => {
    dispatch(fetchWebsites());
  }, [dispatch]);

  // Track user login and check for notifications
  useEffect(() => {
    if (user?.email) {
      const initializeNotifications = async () => {
        // Track login
        await notificationService.trackUserLogin(user.email);

        // Get notification count first
        const count = await notificationService.getUnseenNotificationCount(user.email);
        setNotificationCount(count);

        // Only show welcome back modal if there are unseen notifications
        if (count > 0) {
          const shouldShow = await notificationService.shouldShowWelcomeBack(user.email);
          if (shouldShow) {
            setWelcomeModalOpen(true);
          }
        }
      };

      initializeNotifications();
    }
  }, [user?.email]);

  useEffect(() => {
    setActiveSection(location.pathname.slice(1) || 'dashboard');
  }, [location.pathname]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate(`/${section}`);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <CssBaseline />
      <TopNavigation
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        notificationCount={notificationCount}
        onNotificationClick={() => setWelcomeModalOpen(true)}
        onLogout={handleLogout}
      />
      
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: 3,
          bgcolor: '#f9f9fd',
          minHeight: 'calc(100vh - 64px)'
        }}
      >
        <Outlet />
      </Box>
      
      {/* Welcome Back Modal */}
      {user?.email && (
        <WelcomeBackModal
          open={welcomeModalOpen}
          onClose={() => {
            setWelcomeModalOpen(false);
            // Refresh notification count after closing
            if (user?.email) {
              notificationService.getUnseenNotificationCount(user.email)
                .then(count => setNotificationCount(count));
            }
          }}
          userEmail={user.email}
          userName={(user as any).user_metadata?.name || (user.email ? {
            'newyorknederland@gmail.com': 'Charlotte',
            'newyorkcitycontent@gmail.com': 'Magali',
            'langace@gmail.com': 'Eric',
            'newyorkcitypublicrelations@gmail.com': 'Danielle',
            'finlandnewyork@gmail.com': 'Jaana',
            'james@codeops.nl': 'James',
            'julienewyorkcity@gmail.com': 'Margaux',
          }[user.email] : undefined)}
        />
      )}
    </Box>
  );
};

export default MainLayout;